import React, { useState, useEffect, useCallback } from 'react';
import { getThesis, subscribeThesis, SymbolThesis } from '../../ai/thesisEngine';
import { getOrchestratorStatus, analyzeSymbol, subscribeOrchestratorStatus } from '../../ai/aiOrchestrator';
import { useStore } from '../../store';
import { buildSymbolContextForAI } from '../../services/buildSymbolContext';
import { cooldownRemainingSec } from '../../selectors/viewHelpers';
import { getLastTrigger } from '../../hooks/useAIOrchestration';
import { useStructuredThesis } from '../../domains/ai/thesis2/useStructuredThesis';
import type { StructuredThesis } from '../../domains/ai/thesis2/types';
import { AlertStream } from '../alerts/AlertStream';
import { buildCrossTimeframeContext } from '../../services/crossTimeframe';
import { buildResearchSnapshot, captureAndPersistDecisionSnapshot } from '../../research/researchSnapshot';
import { getSymbolContext } from '../../selectors/symbolContext';
import { evaluateSetupRisk } from '../../risk/riskEngine';
import { createAuditRecord, createTradePlan } from '../../services/auditService';
import { buildJournalDraft } from '../journal/buildJournalDraft';
// Phase 6.2 — live gamma context (optional, non-blocking)
import { useGammaSnapshot }                           from '../gamma/useGammaSnapshot';
import { setLiveGammaContext, clearLiveGammaContext } from '../../domains/ai/prompts/promptBuilder';
import { LIVE_GAMMA_THESIS_ENABLED }                  from '../gamma/gammaUiConfig';
// Phase 6.4 — canonical mapper produces GammaUnifiedContext for the store
import { fromServerSnapshot } from '../../domains/market/engines/gamma/gammaContextMapper';

// Phase 4 -- divergence transparency layer (deterministic engine vs. AI thesis comparison)
import { computeDivergence, DivergenceResult } from '../../domains/divergence/computeDivergence';

// ── Extracted display components ─────────────────────────────────────────────
import { ThesisSummaryCard }   from './components/ThesisSummaryCard';
import { ThesisDeltaCard }     from './components/ThesisDeltaCard';
import { ThesisReasoningList } from './components/ThesisReasoningList';
import { ThesisTriggerHistory }from './components/ThesisTriggerHistory';
import { ConvictionTrendChart }from './components/ConvictionTrendChart';
import { PrevThesisBlock }     from './components/PrevThesisBlock';
import { ThesisExpiryStrip }   from '../../components/trading/ThesisExpiryStrip';
import { ScenarioFramework }   from './components/ScenarioFramework';
import { SizingCard }          from './components/SizingCard';

type PanelTab = 'thesis' | 'signals' | 'history' | 'alerts' | 'thesis2';

// ── Thesis Engine v2 Display Panel ───────────────────────────────────────────
// Additive component — coexists with v1 ThesisPanel. Surfaces the structured,
// gate-validated output of thesis2 pipeline. No engine logic here.

interface Thesis2PanelProps {
  symbol:   string;
  thesis2:  StructuredThesis | null;
  loading:  boolean;
  error:    string | null;
  isStale:  boolean;
  onRun:    () => Promise<void>;
}

const STATE_COLOR: Record<string, string> = {
  ACTIONABLE:  'var(--green)',
  DEVELOPING:  'var(--gold)',
  WAIT:        'var(--blue-dim)',
  HIGH_RISK:   '#f87171',
  INVALIDATED: 'var(--red)',
};

const POSTURE_COLOR: Record<string, string> = {
  LONG:        'var(--green)',
  SHORT:       '#f87171',
  NEUTRAL:     'var(--blue-muted)',
  REDUCE_RISK: '#f87171',
  NO_TRADE:    'var(--blue-dim)',
};

function T2Row({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) {
  if (!value || value === '—') return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      padding: '4px 0', borderBottom: '1px solid var(--border)', gap: 8 }}>
      <span style={{ fontSize: 9, color: 'var(--blue-dim)', fontFamily: 'var(--mono)',
        letterSpacing: '.5px', flexShrink: 0, paddingTop: 2 }}>{label}</span>
      <span style={{ fontSize: 11, color: color ?? 'var(--text)', textAlign: 'right',
        lineHeight: 1.4 }}>{value}</span>
    </div>
  );
}

function T2Section({ title, color, children }: { title: string; color?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: color ?? 'var(--gold)',
        fontFamily: 'var(--mono)', letterSpacing: 1, marginBottom: 5,
        borderBottom: '1px solid var(--border)', paddingBottom: 3 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function ConfBar({ label, value }: { label: string; value: number }) {
  const color = value >= 70 ? 'var(--green)' : value >= 45 ? 'var(--gold)' : '#64748b';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
      <span style={{ fontSize: 9, color: 'var(--blue-dim)', fontFamily: 'var(--mono)',
        minWidth: 96, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 3, background: 'var(--surface2)', borderRadius: 2 }}>
        <div style={{ width: `${value}%`, height: '100%', background: color, borderRadius: 2 }} />
      </div>
      <span style={{ fontSize: 10, color, fontFamily: 'var(--mono)', minWidth: 26 }}>{value}</span>
    </div>
  );
}

function Thesis2Panel({ symbol, thesis2: t, loading, error, isStale, onRun }: Thesis2PanelProps) {
  const [running, setRunning] = React.useState(false);

  const handleRun = async () => {
    setRunning(true);
    try { await onRun(); } finally { setRunning(false); }
  };

  const statusDot = t
    ? <span style={{ fontSize: 9, color: STATE_COLOR[t.thesis_state] ?? 'var(--blue-dim)',
        fontFamily: 'var(--mono)', fontWeight: 700, background: `${STATE_COLOR[t.thesis_state] ?? 'var(--blue-dim)'}18`,
        padding: '1px 6px', borderRadius: 3, border: `1px solid ${STATE_COLOR[t.thesis_state] ?? 'var(--border)'}40` }}>
        {t.thesis_state}
      </span>
    : null;

  return (
    <div style={{ padding: '4px 0' }}>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--purple)',
            fontFamily: 'var(--mono)', letterSpacing: 1 }}>THESIS ENGINE v2</span>
          {statusDot}
          {isStale && t && (
            <span style={{ fontSize: 9, color: 'var(--gold)', fontFamily: 'var(--mono)' }}>⚠ STALE</span>
          )}
        </div>
        <button
          onClick={handleRun}
          disabled={running || loading}
          style={{ fontSize: 9, fontFamily: 'var(--mono)', padding: '3px 10px', borderRadius: 3,
            cursor: running || loading ? 'default' : 'pointer',
            border: '1px solid var(--purple)', color: 'var(--purple)',
            background: running || loading ? 'var(--surface2)' : 'rgba(139,92,246,0.08)',
            fontWeight: 700, letterSpacing: .5 }}
        >
          {running || loading ? 'RUNNING…' : t ? 'RE-RUN' : 'RUN v2'}
        </button>
      </div>

      {error && (
        <div style={{ fontSize: 10, color: 'var(--red)', fontFamily: 'var(--mono)',
          padding: '6px 8px', background: 'var(--red-bg)', borderRadius: 4, marginBottom: 8 }}>
          {error}
        </div>
      )}

      {!t && !loading && !error && (
        <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--blue-dim)',
          fontFamily: 'var(--mono)', fontSize: 11 }}>
          No v2 thesis yet. Click RUN v2 to generate a structured, gate-validated thesis.
        </div>
      )}

      {t && (
        <>
          {/* Decision */}
          <T2Section title="DECISION">
            <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.5, marginBottom: 6 }}>
              {t.decision}
            </div>
            <T2Row label="Posture"   value={t.action_posture} color={POSTURE_COLOR[t.action_posture]} />
            <T2Row label="Setup"     value={t.setup_name} />
            <T2Row label="Bias"      value={t.bias.toUpperCase()}
              color={t.bias === 'bullish' ? 'var(--green)' : t.bias === 'bearish' ? '#f87171' : 'var(--blue-muted)'} />
            <T2Row label="Conviction"   value={`${t.conviction}`}  color="var(--gold)" />
            <T2Row label="Actionability" value={`${t.actionability}`} color="var(--purple)" />
          </T2Section>

          {/* Summary */}
          <T2Section title="SUMMARY" color="var(--blue-muted)">
            <div style={{ fontSize: 11, color: 'var(--text)', lineHeight: 1.6 }}>{t.summary}</div>
          </T2Section>

          {/* Entry plan */}
          <T2Section title="ENTRY PLAN" color="var(--cyan)">
            <T2Row label="Trigger"      value={t.entry_plan.entry_trigger} />
            <T2Row label="Entry hint"   value={t.entry_plan.entry_price_hint ?? '—'} color="var(--gold)" />
            <T2Row label="Confirmation" value={t.entry_plan.confirmation_needed} />
            {t.entry_plan.timing_note && <T2Row label="Timing" value={t.entry_plan.timing_note} />}
          </T2Section>

          {/* Risk plan */}
          <T2Section title="RISK PLAN" color="#f87171">
            <T2Row label="Invalidation"  value={t.risk_plan.invalidation_level} color="#f87171" />
            <T2Row label="Reason"        value={t.risk_plan.invalidation_reason} />
            <T2Row label="Primary risk"  value={t.risk_plan.primary_risk_note} />
            {t.risk_plan.trap_conflict   && <T2Row label="⚠ Trap conflict"   value="Active" color="#f87171" />}
            {t.risk_plan.regime_conflict && <T2Row label="⚠ Regime conflict" value="Active" color="var(--gold)" />}
          </T2Section>

          {/* Targets */}
          {(t.targets.target_1 || t.targets.risk_reward) && (
            <T2Section title="TARGETS" color="var(--green)">
              {t.targets.target_1 && <T2Row label="T1"  value={t.targets.target_1}  color="var(--green)" />}
              {t.targets.target_2 && <T2Row label="T2"  value={t.targets.target_2}  color="var(--green)" />}
              {t.targets.risk_reward != null && (
                <T2Row label="R:R" value={`${t.targets.risk_reward.toFixed(1)}R`} color="var(--purple)" />
              )}
              <T2Row label="Logic" value={t.targets.target_logic} />
            </T2Section>
          )}

          {/* Confidence breakdown */}
          <T2Section title="CONFIDENCE" color="var(--blue-muted)">
            <ConfBar label="Structure"   value={t.confidence.structure_confidence} />
            <ConfBar label="Regime"      value={t.confidence.regime_confidence} />
            <ConfBar label="Liquidity"   value={t.confidence.liquidity_confidence} />
            <ConfBar label="Setup"       value={t.confidence.setup_confidence} />
            <ConfBar label="Risk"        value={t.confidence.risk_confidence} />
            <ConfBar label="Alignment"   value={t.confidence.alignment_confidence} />
            <div style={{ marginTop: 4, display: 'flex', justifyContent: 'space-between',
              padding: '3px 0', borderTop: '1px solid var(--border)' }}>
              <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--blue-muted)',
                fontFamily: 'var(--mono)', letterSpacing: .5 }}>OVERALL</span>
              <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--gold)',
                fontFamily: 'var(--mono)' }}>{t.confidence.overall_confidence}</span>
            </div>
          </T2Section>

          {/* Audit footer */}
          <div style={{ fontSize: 9, color: 'var(--blue-dim)', fontFamily: 'var(--mono)',
            borderTop: '1px solid var(--border)', paddingTop: 6, display: 'flex',
            justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
            <span>{t.audit.validation_status}</span>
            <span>{t.audit.model_id?.split('-').slice(0, 3).join('-')}</span>
            <span>{new Date(t.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        </>
      )}
    </div>
  );
}

export function ThesisPanel({ symbol }: { symbol: string }) {
  const {
    symbolData, settings, weights,
    addAuditRecord, addTradePlan, createJournalDraft, setPendingJournalTab,
    horizon,
  } = useStore();

  const [thesis,    setThesis]    = useState<SymbolThesis>(getThesis(symbol));
  const [loading,   setLoading]   = useState(false);
  const [activeTab, setActiveTab] = useState<PanelTab>('thesis');
  const [aiStatus,  setAiStatus]  = useState(getOrchestratorStatus());
  const [lastError, setLastError] = useState<string | null>(null);
  const [snapSaved,    setSnapSaved]    = useState<string | null>(null);
  const [planSaved,    setPlanSaved]    = useState<string | null>(null);
  const [draftCreated, setDraftCreated] = useState<string | null>(null);
  const [lastAuditId,  setLastAuditId]  = useState<string | null>(null);
  // Phase 4 -- divergence transparency layer: details panel toggle
  const [divergenceExpanded, setDivergenceExpanded] = useState<boolean>(false);

  // Thesis Engine v2 — structured, gate-validated thesis (additive, coexists with v1)
  const thesis2 = useStructuredThesis(symbol);

  // Phase 6.2 — live gamma context (informational only, non-blocking)
  // Fetches Phase 6.1 gamma snapshot and writes it to the prompt builder store.
  // Gated by LIVE_GAMMA_THESIS_ENABLED — disable centrally in gammaUiConfig.ts.
  // Clears the store entry on symbol change and on unmount to prevent stale orphans.
  const { snapshot: liveGamma } = useGammaSnapshot(
    LIVE_GAMMA_THESIS_ENABLED ? symbol : '',
    { autoRefreshMs: LIVE_GAMMA_THESIS_ENABLED ? 30_000 : 0 }
  );
  useEffect(() => {
    if (!LIVE_GAMMA_THESIS_ENABLED || !liveGamma) return;

    // Phase 6.4: use canonical mapper → GammaUnifiedContext
    setLiveGammaContext(symbol, fromServerSnapshot(liveGamma));

    // Clear on symbol change
    return () => { clearLiveGammaContext(symbol); };
  }, [symbol, liveGamma]);

  const cur         = thesis.current;
  const prev        = thesis.previous;
  const lastTrigger = getLastTrigger(symbol);
  const cooldownSec = cooldownRemainingSec(thesis.lastUpdatedAt, 30000);
  const inFlight    = aiStatus.inFlight.has(symbol);
  const symCtx      = getSymbolContext(symbol, symbolData[symbol], settings, weights);

  // Phase 4 -- divergence transparency layer
  // Compares the AI thesis (cur) against the deterministic primary setup
  // (symCtx.setups[0]). The result is rendered as a badge + details panel
  // so the user sees when AI and engine disagree, and why.
  // NOTE (May 9): symCtx.setups is populated by the legacy v1 setup engine
  // (buildSymbolContext.ts -> engines/setup/setupEngine.ts). The v1 engine
  // returns [] when no setups are found, NOT an NVS sentinel. So when the
  // engine evaluates a symbol to NO_VALID_SETUP (visible in the Setups tab
  // via a separate setup2 read), bestSetup here is null, computeDivergence's
  // !ai || !setup early-return fires, and the divergence badge stays hidden.
  // This is a known plumbing gap -- see Phase 2 Backlog in computeDivergence.ts.
  // Fix is upstream: migrate symCtx to read setup2, or pass a parallel NVS flag.
  const bestSetup  = symCtx?.setups[0] ?? null;
  const divergence: DivergenceResult = computeDivergence(cur, bestSetup);

  useEffect(() => {
    const unsubT = subscribeThesis(() => setThesis({ ...getThesis(symbol) }));
    const unsubA = subscribeOrchestratorStatus(() => setAiStatus(getOrchestratorStatus()));
    return () => { unsubT(); unsubA(); };
  }, [symbol]);

  useEffect(() => { setThesis(getThesis(symbol)); setLastError(null); }, [symbol]);

  const runAnalysis = useCallback(async (force = true) => {
    const data = symbolData[symbol];
    if (!data?.candles?.length) return;
    setLoading(true); setLastError(null);
    const ctx = buildSymbolContextForAI(symbol, data, settings, weights);
    if (!ctx) { setLoading(false); return; }
    const result = await analyzeSymbol(ctx, force);
    if (!result.success) setLastError((result as { success: false; error: string; fallback: any }).error);
    setLoading(false);
  }, [symbol, symbolData, settings, weights]);

  const captureSnapshot = useCallback(async () => {
    const data = symbolData[symbol];
    const ctx  = getSymbolContext(symbol, data, settings, weights);
    if (!ctx) return;
    const bestSetup = ctx.setups[0] ?? null;
    const risk      = bestSetup ? evaluateSetupRisk(bestSetup, ctx, cur?.conviction ?? 0) : null;
    const snapshot  = buildResearchSnapshot(ctx, cur ?? null, bestSetup, risk);
    await captureAndPersistDecisionSnapshot(snapshot, cur?.inputHash ?? null);
    if (bestSetup && risk) {
      const auditRecord = createAuditRecord(bestSetup, ctx, risk);
      addAuditRecord(auditRecord);
      setLastAuditId(auditRecord.id);
    }
    setSnapSaved('✓ Saved');
    setTimeout(() => setSnapSaved(null), 2500);
  }, [symbol, symbolData, settings, weights, cur, addAuditRecord]);

  const saveTradePlan = useCallback(() => {
    const data = symbolData[symbol];
    const ctx  = getSymbolContext(symbol, data, settings, weights);
    if (!ctx) return;
    const bestSetup = ctx.setups[0] ?? null;
    if (!bestSetup) return;
    const risk = evaluateSetupRisk(bestSetup, ctx, cur?.conviction ?? 0);
    const plan = createTradePlan(bestSetup, ctx, risk, cur ?? null);
    if (lastAuditId) plan.linkedAuditId = lastAuditId;
    addTradePlan(plan);
    setPlanSaved('✓ Plan Saved');
    setTimeout(() => setPlanSaved(null), 2500);
  }, [symbol, symbolData, settings, weights, cur, addTradePlan, lastAuditId]);

  const createDraft = useCallback(() => {
    const data = symbolData[symbol];
    const ctx  = getSymbolContext(symbol, data, settings, weights);
    if (!ctx) return;
    const draft = buildJournalDraft(ctx, cur ?? null);
    createJournalDraft(draft);
    setPendingJournalTab(true);
    setDraftCreated('✓ Draft Created');
    setTimeout(() => setDraftCreated(null), 2500);
  }, [symbol, symbolData, settings, weights, cur, createJournalDraft, setPendingJournalTab]);


  return (
    <div className="card">
      {/* Title */}
      <div className="sl" style={{ marginBottom: 8 }}>AI Thesis — {symbol}</div>

      {/* ── Action bar — top-center horizontal strip ── */}
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        flexWrap: 'wrap', gap: 10,
        paddingBottom: 12,
        borderBottom: '1px solid var(--border)',
        marginBottom: 12,
      }}>
        {/* Primary: Run Analysis */}
        <button
          className="btn btn-primary btn-sm"
          onClick={() => runAnalysis(true)}
          disabled={loading || inFlight}
          style={{ fontSize: 10, fontFamily: 'var(--mono)', padding: '4px 14px' }}
        >
          {loading || inFlight ? '⟳ Analyzing…' : '⚡ Run Analysis'}
        </button>

        {/* Secondary: Save Snapshot */}
        <button
          className="btn btn-sm"
          onClick={captureSnapshot}
          title="Save decision snapshot to localStorage + SQLite"
          disabled={!cur}
          style={{
            fontSize: 10, fontFamily: 'var(--mono)', padding: '4px 12px',
            color:        snapSaved ? 'var(--green)' : 'var(--blue-dim)',
            borderColor:  snapSaved ? 'var(--green)' : undefined,
            opacity:      cur ? 1 : 0.35,
          }}
        >
          {snapSaved ?? '⬇ Save Snapshot'}
        </button>

        {/* Secondary: Save Plan */}
        <button
          className="btn btn-sm"
          onClick={saveTradePlan}
          title="Save a descriptive trade plan for this setup (decision support only)"
          disabled={!cur}
          style={{
            fontSize: 10, fontFamily: 'var(--mono)', padding: '4px 12px',
            color:       planSaved ? 'var(--green)' : 'var(--amber)',
            borderColor: planSaved ? 'var(--green)' : 'var(--amber-bd, #a16207)',
            opacity:     cur ? 1 : 0.35,
          }}
        >
          {planSaved ?? '📋 Save Plan'}
        </button>

        {/* Tertiary: Journal Draft */}
        <button
          className="btn btn-sm"
          onClick={createDraft}
          title="Create a prefilled journal draft from current setup + thesis"
          disabled={!cur}
          style={{
            fontSize: 10, fontFamily: 'var(--mono)', padding: '4px 12px',
            color:       draftCreated ? 'var(--green)' : 'var(--cyan)',
            borderColor: draftCreated ? 'var(--green)' : 'var(--cyan-bd, #0e7490)',
            opacity:     cur ? 1 : 0.35,
          }}
        >
          {draftCreated ?? '📓 Journal Draft'}
        </button>

        {/* Cooldown indicator */}
        {cooldownSec > 0 && !loading && (
          <span style={{ fontSize: 9, color: 'var(--blue-dim)', fontFamily: 'var(--mono)' }}>
            cooldown {cooldownSec}s
          </span>
        )}
      </div>

      {/* Phase 3 commit 3.5: thesis expiry contract — addresses tester
          complaint about open-ended monitoring ("two-hour vigil"). Renders
          relative + absolute time, collapsible invalidation conditions,
          per-horizon fallback when AI omitted the field. */}
      {cur && (
        <ThesisExpiryStrip
          thesisExpiry={cur.thesisExpiry}
          traderHorizon={horizon}
          generatedAt={cur.timestamp}
        />
      )}

      {/* Summary card — sits below action bar */}
      {cur && <ThesisSummaryCard bias={cur.overallBias} conviction={cur.conviction} />}

      {/* Phase 4 -- divergence transparency layer: badge.
          Renders only when AI thesis disagrees with the deterministic primary
          setup. Click toggles the details panel below the action plan.
          Severity:
            'direction' | 'both' -> red (AI contradicts engine on direction)
            'levels'             -> amber (entry/invalidation/T1 differ >0.5%) */}
      {cur && (divergence.level !== 'none' || divergence.noValidSetup) && (
        <button
          onClick={() => setDivergenceExpanded(v => !v)}
          aria-expanded={divergenceExpanded}
          aria-controls="divergence-details"
          style={{
            width: '100%',
            marginBottom: 8,
            padding: '6px 10px',
            fontSize: 11,
            fontFamily: 'var(--mono)',
            borderRadius: 3,
            border: '1px solid',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            textAlign: 'left',
            color:           divergence.directionConflict ? 'var(--red)'    : 'var(--amber)',
            borderColor:     divergence.directionConflict ? 'var(--red)'    : 'var(--amber-bd, #a16207)',
            backgroundColor: divergence.directionConflict ? 'rgba(239, 68, 68, 0.08)' : 'rgba(217, 119, 6, 0.08)',
          }}
          title={divergence.noValidSetup
            ? 'Engine: no valid setup. AI thesis present. Click to inspect.'
            : "AI thesis levels differ from the engine's primary setup. Click to inspect."}
        >
          <span style={{ fontWeight: 600 }}>
            {divergence.noValidSetup
              ? 'Engine: no valid setup'
              : divergence.directionConflict
                ? 'DIRECTION CONTRADICTS PRIMARY SETUP'
                : 'Levels diverge from primary setup'}
          </span>
          <span style={{ opacity: 0.75, fontSize: 10 }}>
            {divergenceExpanded ? 'hide details' : 'click to see why'}
          </span>
        </button>
      )}

      {/* Phase 4 -- divergence transparency layer: details panel.
          Renders when the badge is clicked. Shows direction comparison,
          per-field level divergence, and the AI's stated reason for
          overriding (or explicit "did not provide a reason" if absent).
          Silent overrides are worse than explained ones; making the
          absence visible is the whole point of this layer. */}
      {cur && (divergence.level !== 'none' || divergence.noValidSetup) && divergenceExpanded && (
        <div
          id="divergence-details"
          style={{
            marginBottom: 8,
            padding: '10px 12px',
            fontSize: 10,
            fontFamily: 'var(--mono)',
            border: '1px solid',
            borderColor: divergence.directionConflict ? 'var(--red)' : 'var(--amber-bd, #a16207)',
            borderLeft: divergence.directionConflict ? '3px solid var(--red)' : '3px solid var(--amber)',
            borderRadius: 3,
            backgroundColor: 'var(--surface2)',
            color: 'var(--text)',
            lineHeight: 1.5,
          }}
        >
          {/* Direction comparison */}
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontWeight: 600, color: 'var(--blue-dim)', fontSize: 9, letterSpacing: '.5px', marginBottom: 4 }}>
              DIRECTION
            </div>
            <div>
              AI:{'\u00A0'}
              <span style={{ color: divergence.directionConflict ? 'var(--red)' : 'var(--text)', fontWeight: 600 }}>
                {divergence.aiBias.toUpperCase()}
              </span>
              {'\u00A0\u00A0\u2022\u00A0\u00A0'}
              Engine:{'\u00A0'}
              <span style={{ color: 'var(--text)', fontWeight: 600 }}>
                {divergence.noValidSetup
                  ? 'NO VALID SETUP'
                  : divergence.setupDir
                    ? divergence.setupDir.toUpperCase()
                    : '(no setup)'}
              </span>
              {divergence.directionConflict && (
                <span style={{ color: 'var(--red)', marginLeft: 8 }}>
                  {'\u2192'} CONTRADICTS
                </span>
              )}
            </div>
          </div>

          {/* Per-field level comparison */}
          {divergence.noValidSetup ? (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 600, color: 'var(--blue-dim)', fontSize: 9, letterSpacing: '.5px', marginBottom: 4 }}>
                LEVELS
              </div>
              <div style={{ color: 'var(--blue-dim)', fontStyle: 'italic' }}>
                Level comparison not applicable -- engine has no valid setup.
              </div>
            </div>
          ) : Object.keys(divergence.fields).length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 600, color: 'var(--blue-dim)', fontSize: 9, letterSpacing: '.5px', marginBottom: 4 }}>
                LEVELS
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr 70px', gap: '4px 12px', alignItems: 'baseline' }}>
                <div style={{ color: 'var(--blue-dim)', fontSize: 9 }}>FIELD</div>
                <div style={{ color: 'var(--blue-dim)', fontSize: 9 }}>AI</div>
                <div style={{ color: 'var(--blue-dim)', fontSize: 9 }}>ENGINE</div>
                <div style={{ color: 'var(--blue-dim)', fontSize: 9, textAlign: 'right' }}>{'\u0394 %'}</div>

                {(['entry', 'invalidation', 'target1'] as const).map((key) => {
                  const field = divergence.fields[key];
                  if (!field) return null;
                  const label = key === 'target1' ? 'T1' : key.charAt(0).toUpperCase() + key.slice(1);
                  const diffStr = field.diffPct == null ? 'n/a' : field.diffPct.toFixed(2) + '%';
                  return (
                    <React.Fragment key={key}>
                      <div style={{ color: 'var(--text)' }}>{label}</div>
                      <div style={{ color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {field.aiNumeric != null ? field.aiNumeric.toFixed(2) : '(unparsed)'}
                      </div>
                      <div style={{ color: 'var(--text)' }}>
                        {field.engineNumeric != null ? field.engineNumeric.toFixed(2) : '(none)'}
                      </div>
                      <div style={{
                        color: field.diverges ? (divergence.directionConflict ? 'var(--red)' : 'var(--amber)') : 'var(--text)',
                        textAlign: 'right',
                        fontWeight: field.diverges ? 600 : 400,
                      }}>
                        {diffStr}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          {/* AI uncertainty / context (panel section) */}
          <div>
            <div style={{ fontWeight: 600, color: 'var(--blue-dim)', fontSize: 9, letterSpacing: '.5px', marginBottom: 4 }}>
              AI UNCERTAINTY / CONTEXT
            </div>
            {divergence.overrideReasonPresent && divergence.overrideReasonText ? (
              <div style={{ color: 'var(--text)', fontStyle: 'italic' }}>
                "{divergence.overrideReasonText}"
              </div>
            ) : (
              <div style={{ color: 'var(--red)', fontStyle: 'italic' }}>
                AI provided no additional uncertainty or contextual commentary alongside this divergence.
              </div>
            )}
          </div>

          {/* Footer: tolerance reference */}
          <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px dashed var(--border)', fontSize: 9, color: 'var(--blue-dim)' }}>
            Level divergence threshold: 0.5%. Direction conflicts are always flagged.
          </div>
        </div>
      )}

      {/* Status row */}
      {cur && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
          <ThesisDeltaCard change={cur.thesisChange} />
          <span style={{ fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--text)',
            border: '1px solid var(--border)', background: 'var(--surface2)', padding: '1px 6px', borderRadius: 3 }}>
            ACT {cur.actionability}%
          </span>
          <span style={{ fontSize: 9, fontFamily: 'var(--mono)',
            color: cur.shouldTradeNow && (symCtx?.setupActionable ?? true) ? 'var(--green)'
              : cur.shouldTradeNow ? 'var(--amber)' : 'var(--blue-dim)',
            border: `1px solid ${cur.shouldTradeNow && (symCtx?.setupActionable ?? true) ? 'var(--green-bd)'
              : cur.shouldTradeNow ? 'var(--amber)44' : 'var(--border)'}`,
            background: cur.shouldTradeNow && (symCtx?.setupActionable ?? true) ? 'var(--green-bg)' : 'var(--surface2)',
            padding: '1px 6px', borderRadius: 3 }}>
            {cur.shouldTradeNow && (symCtx?.setupActionable ?? true)
              ? '● TRADEABLE NOW'
              : cur.shouldTradeNow && symCtx && !symCtx.setupActionable
              ? '◐ WATCH FOR CONFIRMATION'
              : '○ WAIT'}
          </span>
          <span style={{ fontSize: 9, fontFamily: 'var(--mono)', letterSpacing: '.5px',
            color: cur.urgency === 'high' ? 'var(--red)' : cur.urgency === 'medium' ? 'var(--amber)' : 'var(--blue-dim)' }}>
            {cur.urgency.toUpperCase()} URGENCY
          </span>
        </div>
      )}

      {thesis.history.length > 0 && <div style={{ marginBottom: 10 }}><ConvictionTrendChart thesis={thesis} /></div>}

      {lastTrigger && (
        <div style={{ fontSize: 9, color: 'var(--blue-dim)', fontFamily: 'var(--mono)', marginBottom: 8 }}>
          Last refresh: {lastTrigger}
        </div>
      )}

      {lastError && (
        <div style={{ padding: '10px 14px', background: 'var(--red-bg)', border: '1px solid var(--red-bd)', borderRadius: 8, marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--red)', fontWeight: 700, marginBottom: 6, fontFamily: 'var(--mono)', letterSpacing: '.5px' }}>AI ANALYSIS UNAVAILABLE</div>
          <div style={{ fontSize: 11, color: 'var(--t1)', lineHeight: 1.7, marginBottom: 8 }}>
            The AI Analyst could not complete the analysis. This may be due to a connectivity issue,
            an invalid or expired API key, or a temporary service interruption.
            Please verify your API credentials are correctly configured and try again.
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-sm" onClick={() => setLastError(null)} style={{ fontSize: 10, fontFamily: 'var(--mono)' }}>Dismiss</button>
            <button className="btn btn-primary btn-sm" onClick={() => runAnalysis(true)} style={{ fontSize: 10, fontFamily: 'var(--mono)' }}>↻ Retry</button>
          </div>
        </div>
      )}

      {!cur && !loading && !lastError && (
        <div className="empty" style={{ padding: '20px' }}>
          <div className="icon">🤖</div><h3>NO THESIS YET</h3>
          <p>Click "Run Analysis" to generate an AI trade thesis for {symbol}.</p>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--purple)', fontFamily: 'var(--mono)', fontSize: 11 }}>
          ⟳ Reasoning over {symbol} engine outputs…
        </div>
      )}

      {cur && !loading && (
        <>
          <div className="tab-row" style={{ marginBottom: 10 }}>
            {([ ['thesis','Thesis'], ['signals','Signals'], ['history',`History (${thesis.history.length})`], ['alerts','Alerts'] ] as [PanelTab,string][]).map(([id, l]) => (
              <button key={id} className={`tab ${activeTab === id ? 'on' : ''}`} onClick={() => setActiveTab(id)}>{l}</button>
            ))}
            {/* Thesis v2 tab disabled (cleanup pass 3/3): the v2 pipeline at
                src/domains/ai/thesis2/ does browser->Anthropic calls directly
                using a per-user API key from settings, which is bad UX for
                a multi-tester product. Architecture choice needs revisiting
                before re-enabling. Hide here, keep code path so re-enabling
                is just removing this `false &&` wrap. */}
            {false && (
            <button
              className={`tab ${activeTab === 'thesis2' ? 'on' : ''}`}
              onClick={() => setActiveTab('thesis2')}
              style={{ position: 'relative' }}
              title="Structured Thesis Engine v2 — gate-validated, schema-enforced"
            >
              Thesis v2
              <span style={{
                position: 'absolute', top: 3, right: 3,
                width: 4, height: 4, borderRadius: '50%',
                background: thesis2.current ? 'var(--green)' : 'var(--blue-dim)',
                opacity: .8,
              }} />
            </button>
            )}
          </div>

          {/* ── THESIS TAB ─────────────────────────────────────────────── */}
          {activeTab === 'thesis' && (
            <div>
              {prev && <PrevThesisBlock prev={prev} cur={cur} />}

              {/* Primary thesis block */}
              <div style={{ padding: '10px 14px', background: 'var(--surface2)',
                borderLeft: `3px solid ${cur.overallBias === 'bullish' ? 'var(--green)' : cur.overallBias === 'bearish' ? 'var(--red)' : 'var(--amber)'}`,
                borderRadius: '0 6px 6px 0', marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.7 }}>{cur.primaryThesis}</div>
                <div style={{ fontSize: 9, color: 'var(--blue-dim)', marginTop: 4, fontFamily: 'var(--mono)' }}>
                  {cur.timeHorizon.replace('_', ' ')} horizon · {new Date(cur.timestamp).toLocaleTimeString()}
                </div>
              </div>

              {cur.whatChanged.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--gold)', fontWeight: 700, marginBottom: 4, letterSpacing: '1px' }}>WHAT CHANGED</div>
                  {cur.whatChanged.map((c, i) => <div key={i} style={{ fontSize: 11, color: 'var(--t1)', marginBottom: 2 }}>· {c}</div>)}
                </div>
              )}

              {/* Action plan */}
              <div style={{ padding: '10px 12px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, marginBottom: 10 }}>
                <div style={{ fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--gold)', fontWeight: 700, marginBottom: 8, letterSpacing: '1px' }}>ACTION PLAN</div>
                {[
                  { l: 'SETUP TYPE',   v: cur.bestSetupType.replace(/_/g,' ').toUpperCase(), c: 'var(--cyan)'       },
                  { l: 'ENTRY',        v: cur.entryLogic,          c: 'var(--blue-muted)' },
                  { l: 'CONFIRMATION', v: cur.confirmationNeeded,  c: 'var(--amber)'      },
                  { l: 'INVALIDATION', v: cur.invalidation,        c: 'var(--red)'        },
                  { l: 'TARGET',       v: cur.targetLogic,         c: 'var(--green)'      },
                  { l: 'PRIMARY RISK', v: cur.primaryRisk,         c: 'var(--amber)'      },
                ].map(({ l, v, c }) => (
                  <div key={l} style={{ marginBottom: 6 }}>
                    <div style={{ fontSize: 8, fontFamily: 'var(--mono)', color: 'var(--blue-dim)', letterSpacing: '.8px' }}>{l}</div>
                    <div style={{ fontSize: 11, color: c, lineHeight: 1.5 }}>{v}</div>
                  </div>
                ))}
              </div>

              <div className="disclaimer">{cur.uncertainty}</div>
              <ScenarioFramework thesis={cur} />
              {(() => {
                const sCtx = buildSymbolContextForAI(symbol, symbolData[symbol], settings, weights);
                return sCtx ? <SizingCard thesis={cur} ctx={sCtx} /> : null;
              })()}
            </div>
          )}

          {/* ── SIGNALS TAB ────────────────────────────────────────────── */}
          {activeTab === 'signals' && (
            <div>
              <ThesisReasoningList label="Regime Read"    content={cur.regimeRead}    />
              <ThesisReasoningList label="SMC Read"       content={cur.smcRead}       />
              <ThesisReasoningList label="Liquidity Read" content={cur.liquidityRead} />
              <ThesisReasoningList label="Trap Read"      content={cur.trapRead}      color="var(--amber)" />
              <ThesisReasoningList label="Gamma Read"     content={cur.gammaRead}     />
              {symbolData[symbol]?.candles?.length ? (() => {
                const smcBias = thesis.current?.overallBias ?? 'neutral';
                const htf = buildCrossTimeframeContext(
                  symbolData[symbol]!.candles!,
                  settings.defaultInterval,
                  smcBias === 'bullish' ? 'bullish' : smcBias === 'bearish' ? 'bearish' : 'neutral'
                );
                if (!htf?.hasEnoughBars) return null;
                return <ThesisReasoningList label={`${htf.higherTf} Context (${htf.confidence})`} content={`${htf.alignmentNote} ${htf.structureNote}`} />;
              })() : null}
              {cur.keyDrivers.length > 0 && (
                <div>
                  <div style={{ fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--gold)', fontWeight: 700, marginBottom: 4, letterSpacing: '1px' }}>KEY DRIVERS</div>
                  {cur.keyDrivers.map((d, i) => (
                    <div key={i} style={{ fontSize: 11, color: 'var(--text)', marginBottom: 3 }}>
                      <span style={{ color: 'var(--gold)', marginRight: 6, fontFamily: 'var(--mono)' }}>{i + 1}.</span>{d}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── HISTORY TAB ────────────────────────────────────────────── */}
          {activeTab === 'history' && (
            <div>
              {thesis.history.length === 0
                ? <div style={{ textAlign: 'center', padding: 16, color: 'var(--blue-dim)', fontFamily: 'var(--mono)', fontSize: 11 }}>NO HISTORY YET — History accumulates as AI re-analyzes.</div>
                : thesis.history.map((entry, i) => <ThesisTriggerHistory key={i} entry={entry} />)
              }
            </div>
          )}

          {activeTab === 'alerts' && <AlertStream symbol={symbol} maxHeight={300} />}

          {/* ── THESIS V2 TAB ───────────────────────────────────────── */}
          {activeTab === 'thesis2' && (
            <Thesis2Panel
              symbol={symbol}
              thesis2={thesis2.current}
              loading={thesis2.loading}
              error={thesis2.error}
              isStale={thesis2.isStale}
              onRun={thesis2.runAnalysis}
            />
          )}
        </>
      )}
    </div>
  );
}
